import { HumanSupportScreenshotSchema, SUPPORT_SCREENSHOT_BYTES, type HumanSupportScreenshot } from "@cross-border-agent/contracts";

export async function normalizeSupportImage(dataUrl: string, meta: Omit<HumanSupportScreenshot, "id" | "dataUrl">): Promise<HumanSupportScreenshot> {
  if (dataUrl.length > 12 * 1024 * 1024 || !/^data:image\/(png|jpeg|webp);base64,/.test(dataUrl)) throw new Error("请选择 PNG、JPEG 或 WebP 图片，原文件不超过 8 MB。");
  const image = new Image();
  await new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => { image.onload = null; image.onerror = null; image.src = ""; reject(new Error("图片处理超时，请裁剪到问题区域后重试。")); }, 10000);
    image.onload = () => { clearTimeout(timer); resolve(); };
    image.onerror = () => { clearTimeout(timer); reject(new Error("图片无法读取，请重新选择。")); };
    image.src = dataUrl;
  });
  if (!image.naturalWidth || !image.naturalHeight || image.naturalWidth * image.naturalHeight > 40000000) throw new Error("图片尺寸过大，请截取问题区域后上传。");
  const scale = Math.min(1, 1600 / Math.max(image.naturalWidth, image.naturalHeight));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale)); canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
  const context = canvas.getContext("2d");
  if (!context) throw new Error("浏览器暂时无法处理图片，请重试。");
  context.fillStyle = "#fff"; context.fillRect(0, 0, canvas.width, canvas.height); context.drawImage(image, 0, 0, canvas.width, canvas.height);
  // Re-encoding strips EXIF/file metadata. This does not remove private information visible in pixels.
  for (const quality of [0.86, 0.72, 0.55]) {
    const encoded = canvas.toDataURL("image/jpeg", quality);
    if (encoded.length <= 4 * Math.ceil(SUPPORT_SCREENSHOT_BYTES / 3) + 23) return HumanSupportScreenshotSchema.parse({ ...meta, id: crypto.randomUUID(), dataUrl: encoded });
  }
  throw new Error("压缩后图片仍超过 512 KB，请裁剪到问题区域后上传。");
}

export async function uploadSupportImage(file: File): Promise<HumanSupportScreenshot> {
  if (!["image/png", "image/jpeg", "image/webp"].includes(file.type) || file.size > 8 * 1024 * 1024) throw new Error("请选择 PNG、JPEG 或 WebP 图片，原文件不超过 8 MB。");
  const data = await new Promise<string>((resolve, reject) => { const reader = new FileReader(); reader.onload = () => resolve(String(reader.result)); reader.onerror = () => reject(new Error("文件读取失败，请重试。")); reader.readAsDataURL(file); });
  return normalizeSupportImage(data, { source: "upload", addedAt: new Date().toISOString() });
}
